"use client";

/**
 * Avatar animé des personas, rendu SVG du moteur « bloub » (src/lib/bloub,
 * le même code que l'app mobile : `engine.sample(t)` est une fonction pure du
 * temps). Une image par tour de boucle rAF, injectée dans le DOM sans passer
 * par React : le corps (encre), les yeux (trous couleur du fond, rognés par la
 * silhouette), les points et les anneaux. Cadence réglable ; en pause hors
 * écran et quand l'onglet est caché.
 */
import { useEffect, useMemo, useRef } from "react";
import { BotEngine, type BotFrame } from "@/lib/bloub/engine";
import { defaultCycle, type Block } from "@/lib/bloub/cycles";
import { EXPRESSION_BY_ID, type ExpressionId } from "@/lib/bloub/expressions";
import { DEMI_VIEWBOX, RAYON } from "@/lib/bloub/repere";
import { mixHex, SHAPE_BY_ID, type ShapeId } from "@/lib/bloub/skins";
import type { StateId } from "@/lib/bloub/states";

/** Amplitude du regard (degrés) pour une cible en bord d'avatar. */
const LOOK_YAW = 16;
const LOOK_PITCH = 12;

export interface LookTarget {
  x: number;
  y: number;
}

export interface BloubAvatarProps {
  shape: ShapeId;
  expression: ExpressionId;
  /** Encre du corps. */
  ink: string;
  /** Couleur derrière l'avatar : les yeux sont des trous. */
  paper: string;
  /** Côté (px CSS). */
  size: number;
  /** État tenu ; absent = montage (`cycle`) en boucle. */
  state?: StateId;
  cycle?: Block[];
  /** Images par seconde (défaut 30). */
  fps?: number;
  /** Cible du regard, lue à chaque image (−1…1 sur chaque axe), `null` = libre. */
  look?: React.RefObject<LookTarget | null>;
  paused?: boolean;
  className?: string;
  title?: string;
}

const VB = DEMI_VIEWBOX;

function esc(s: string): string {
  return s.replace(/"/g, "&quot;");
}

/** Une image → balisage SVG (repère centré : viewBox −VB…VB). */
function render(frame: BotFrame, ink: string, paper: string, clipId: string): string {
  const parts: string[] = [];
  const arc = (d: string, a: BotFrame["arcs"][number]) => {
    const stroke = a.grad.stops[0] ?? ink;
    parts.push(
      `<path d="${esc(d)}" fill="none" stroke="${esc(stroke)}" stroke-width="${a.width}" stroke-linecap="round" opacity="${a.opacity}"/>`,
    );
  };
  const dots = () => {
    for (const d of frame.dots) {
      const fill = d.color ?? (d.depth === undefined ? ink : mixHex(paper, ink, d.depth));
      if (d.d) {
        parts.push(
          `<path d="${esc(d.d)}" fill="${esc(fill)}" opacity="${d.opacity}" transform="translate(${d.x} ${d.y}) rotate(${d.rot ?? 0}) scale(${RAYON})"/>`,
        );
      } else {
        parts.push(`<circle cx="${d.x}" cy="${d.y}" r="${d.r}" fill="${esc(fill)}" opacity="${d.opacity}"/>`);
      }
    }
  };

  for (const a of frame.arcs) arc(a.back, a);
  if (frame.dotsBehind) dots();

  parts.push(`<clipPath id="${clipId}"><path d="${esc(frame.bodyPath)}"/></clipPath>`);
  parts.push(`<g opacity="${frame.bodyAlpha}">`);
  parts.push(`<path d="${esc(frame.bodyPath)}" fill="${esc(ink)}"/>`);
  parts.push(`<g clip-path="url(#${clipId})">`);
  for (const eye of frame.eyes) {
    parts.push(`<path d="${esc(eye.d)}" transform="${esc(eye.matrix)}" fill="${esc(paper)}" opacity="${eye.alpha}"/>`);
  }
  if (frame.notch) {
    parts.push(`<circle cx="${frame.notch.x}" cy="${frame.notch.y}" r="${frame.notch.r}" fill="${esc(paper)}"/>`);
  }
  parts.push(`</g></g>`);

  if (!frame.dotsBehind) dots();
  if (frame.notif) {
    parts.push(`<circle cx="${frame.notif.x}" cy="${frame.notif.y}" r="${frame.notif.r}" fill="#2f7cf6"/>`);
  }
  for (const a of frame.arcs) arc(a.front, a);
  return parts.join("");
}

let clipSeq = 0;

export function BloubAvatar({
  shape,
  expression,
  ink,
  paper,
  size,
  state,
  cycle,
  fps = 30,
  look,
  paused = false,
  className,
  title,
}: BloubAvatarProps) {
  const groupRef = useRef<SVGGElement>(null);
  const clipId = useMemo(() => `bloub-clip-${++clipSeq}`, []);
  const clock = useRef(0);
  const origin = useRef(0);

  const engine = useMemo(() => {
    const radii = SHAPE_BY_ID.get(shape)?.radii ?? null;
    const bot = new BotEngine(RAYON, state ?? "idle", radii, EXPRESSION_BY_ID.get(expression) ?? null);
    // Nos personas ne sont pas des boules : yeux toujours verticaux à l'écran.
    bot.uprightEyes = true;
    return bot;
    // Un moteur par look ; l'état se pilote par `setState`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape]);

  useEffect(() => {
    engine.setExpression(EXPRESSION_BY_ID.get(expression) ?? null, clock.current);
  }, [engine, expression]);
  useEffect(() => {
    if (state) engine.setState(state, clock.current);
  }, [engine, state]);

  useEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    // Première image tout de suite (même en pause).
    g.innerHTML = render(engine.sample(clock.current), ink, paper, clipId);
    if (paused) return;

    let raf = 0;
    let last = 0;
    let visible = !document.hidden;
    const minInterval = 1000 / Math.max(1, fps);
    const blocks: Block[] = state ? [] : (cycle ?? defaultCycle().blocks);
    let blockIndex = 0;
    let blockStart = clock.current;
    if (!state && blocks.length > 0) engine.setState(blocks[0]!.state, clock.current);
    let lastLook: LookTarget | null | undefined;

    const tick = (nowMs: number) => {
      raf = requestAnimationFrame(tick);
      if (!visible) return;
      if (origin.current === 0) origin.current = nowMs - clock.current * 1000;
      if (nowMs - last < minInterval) return;
      last = nowMs;
      const t = (nowMs - origin.current) / 1000;
      clock.current = t;
      if (!state && blocks.length > 0) {
        const current = blocks[blockIndex]!;
        if (t - blockStart >= current.duration) {
          blockIndex = (blockIndex + 1) % blocks.length;
          blockStart = t;
          engine.setState(blocks[blockIndex]!.state, t);
        }
      }
      const target = look?.current ?? null;
      if (target !== lastLook) {
        lastLook = target;
        engine.setLook(
          target
            ? {
                yaw: Math.max(-1, Math.min(1, target.x)) * LOOK_YAW,
                pitch: Math.max(-1, Math.min(1, target.y)) * LOOK_PITCH,
                mix: 1,
                spin: 0,
                wander: 0.3,
              }
            : null,
          t,
        );
      }
      g.innerHTML = render(engine.sample(t), ink, paper, clipId);
    };
    raf = requestAnimationFrame(tick);
    const onVisibility = () => {
      visible = !document.hidden;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      origin.current = 0;
    };
  }, [clipId, cycle, engine, fps, ink, look, paper, paused, state]);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`${-VB} ${-VB} ${VB * 2} ${VB * 2}`}
      className={className}
      role={title ? "img" : "presentation"}
      aria-label={title}
    >
      <g ref={groupRef} />
    </svg>
  );
}
